import React, { useRef, useState } from 'react';
import { useForm } from '../context/FormContext';
import { useAppConfig } from '../context/AppConfigContext';
import { useChatWidgetOffset } from '../hooks/useChatWidgetOffset';
import { getPhaseProgress } from '../funnel/phaseProgress';
import { OperationalBanner } from './operations/OperationalBanner';
import PetsSummaryBar from './funnel/PetsSummaryBar';
import PetFabTab from './funnel/PetFabTab';
import PetDrawer from './funnel/PetDrawer';

interface FunnelLayoutProps {
  children: React.ReactNode;
  step: number;
  totalSteps?: number;
  maxWidth?: 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  onContinue?: () => void;
  onBack?: () => void;
  continueDisabled?: boolean;
  /**
   * Texto opcional que se muestra junto al botón Continuar cuando está
   * deshabilitado, indicando qué falta por rellenar (ej. "Selecciona la raza").
   */
  continueDisabledReason?: string;
  /**
   * Disparado cuando el usuario pulsa Continuar mientras está
   * deshabilitado. La pantalla puede usarlo para marcar campos
   * pendientes (border rojo) y llamar la atención sobre lo que falta.
   */
  onContinueDisabledAttempt?: () => void;
  continueText?: string;
  showBack?: boolean;
  showContinue?: boolean;
  useTwoPhase?: boolean;
  /**
   * Optional contextual content (callouts, "why we ask", hints).
   * On desktop (md+): renders as a sidebar on the right. On mobile:
   * renders below the main content in document flow.
   */
  sidebar?: React.ReactNode;
}

const MAX_WIDTH_CLASSES: Record<string, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
};

// Cuando se usa una pantalla ancha (4xl+), saltamos el grid de 3 columnas
// con phantom y hacemos que la card central use todo el ancho disponible.
// Esto evita que P9 (que ya no usa sidebar) quede atrapado en 32rem.
const WIDE_KEYS = new Set(['4xl', '5xl']);

export const FunnelLayout: React.FC<FunnelLayoutProps> = ({
  children,
  step,
  totalSteps = 14,
  maxWidth = 'lg',
  onContinue,
  onBack,
  continueDisabled = false,
  continueDisabledReason,
  onContinueDisabledAttempt,
  continueText,
  showBack = true,
  showContinue = true,
  useTwoPhase = false,
  sidebar,
}) => {
  const {
    goToPreviousStep,
    setCurrentStep,
    formData,
    petsCompleted,
    currentStep,
    updateFormData,
    updatePetSnapshotData,
    removePetSnapshot,
    replaceCurrentWithPetSnapshot,
    restorePetForEditing,
  } = useForm();
  const { t, logoImage } = useAppConfig();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [firstPetId, setFirstPetId] = useState<string | undefined>(undefined);

  const handlePillClick = (petId: string) => {
    setFirstPetId(petId);
    setIsDrawerOpen(true);
  };

  const handleRemoveCurrentPet = () => {
    const fallbackPet = petsCompleted[petsCompleted.length - 1];
    if (fallbackPet) replaceCurrentWithPetSnapshot(fallbackPet.id, currentStep);
  };

  const handleEditCompletedPet = (petId: string, step: number) => {
    restorePetForEditing(petId, step);
    setIsDrawerOpen(false);
  };

  // Trampa de API: si una pantalla deshabilita el CTA pero olvida el callback,
  // los clicks se silencian sin feedback (regresión vista en Step9 mid-PR).
  if (process.env.NODE_ENV !== 'production' && continueDisabled && !onContinueDisabledAttempt && showContinue) {
    // eslint-disable-next-line no-console
    console.warn(
      '[FunnelLayout] continueDisabled=true sin onContinueDisabledAttempt — los clicks se ignorarán sin feedback al usuario',
    );
  }

  const handleBack = onBack || goToPreviousStep;
  const widthClass = MAX_WIDTH_CLASSES[maxWidth] || 'max-w-lg';

  // En el flujo v2 (14 pantallas) no hay pasos ocultos: P1-P9 usan useTwoPhase
  // y el progreso legacy solo se usa como fallback para pantallas futuras (Fase 2).
  const hiddenSteps: number[] = [];
  const visibleTotal = totalSteps - hiddenSteps.length;
  const hiddenBefore = hiddenSteps.filter((h) => h < step).length;
  const visibleStep = Math.max(1, step - hiddenBefore);
  const legacyProgress = Math.round((visibleStep / visibleTotal) * 100);

  // Two-phase progress (opt-in via useTwoPhase prop)
  // Two-phase mode: percent resets from 100% (pantalla 9) to 20% (pantalla 10) by design —
  // solves the "100% completado pero aún faltan 5 pantallas" bug.
  const phaseProgress = useTwoPhase ? getPhaseProgress(step) : null;
  const progress = phaseProgress ? phaseProgress.percent : legacyProgress;

  // Altura real del bloque superior fijo, para que el widget de chat anclado
  // arriba no se superponga a la cabecera.
  const topChromeRef = useRef<HTMLDivElement>(null);
  useChatWidgetOffset(topChromeRef, 'top');

  // h-screen (=100vh) en iOS Safari/iPad se calcula con la barra de URL
  // oculta y deja la bottom nav fuera del viewport visible al hacer scroll.
  // 100dvh (dynamic viewport height) sigue al viewport real, así que la
  // CTA siempre se ve. h-screen queda como fallback CSS para navegadores
  // antiguos. El style inline con 100dvh siempre gana en orden de cascada
  // (las clases Tailwind se ordenan alfabéticamente y h-[100dvh] perdía
  // contra h-screen — Codex review 2026-04-28).
  return (
    <>
      <div className="h-screen bg-[#F7F4EE] flex flex-col overflow-hidden" style={{ height: '100dvh' }}>
        {/* Este bloque superior es fijo dentro del shell de altura 100dvh (el
            contenido scrollea por dentro), así que un widget de chat anclado
            arriba lo taparía de forma permanente. Publicamos su altura real
            para que se coloque justo debajo. */}
        <div ref={topChromeRef} className="flex-shrink-0">
          <OperationalBanner />
          {/* Header */}
          <header className="bg-white px-4 sm:px-6 py-1 flex items-center justify-between flex-shrink-0">
            <img
              src={logoImage}
              alt="Milo & Lola"
              className="h-8 sm:h-10 object-contain cursor-pointer"
              onClick={() => setCurrentStep(1)}
            />
            <div className="flex items-center gap-3 sm:gap-4">
              {phaseProgress ? (
                <span className="font-fredoka text-black/50 text-xs">
                  {t(`funnel.phase_label_${phaseProgress.phase}`, phaseProgress.label)} · {phaseProgress.current}/
                  {phaseProgress.total}
                </span>
              ) : (
                <span className="font-fredoka text-black text-sm">
                  {t('funnel.step_counter', 'Paso {step} de {totalSteps}', {
                    step: visibleStep,
                    totalSteps: visibleTotal,
                  })}
                </span>
              )}
              <span className="font-fredoka text-[#008BBC] text-sm font-medium">
                {t('funnel.progress', '{progress}% completado', { progress })}
              </span>
            </div>
          </header>

          {/* Progress bar */}
          <div className="h-1.5 bg-[#E9E3D7]/60 flex-shrink-0">
            <div className="h-full bg-[#008BBC] transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>

          {/* Multi-pet bar — inside the h-dvh container so it doesn't push FunnelLayout off-screen */}
          <PetsSummaryBar onPillClick={handlePillClick} />
        </div>

        {/* Content
        3-col grid (phantom left | center card | sidebar right) ONLY on lg+
        (≥1024px) where the side columns fit comfortably. On mobile + tablet
        (<lg) we fall back to a single-column layout — pantallas already render
        the same SoWhatCallout content inline via `md:hidden` divs, so no info
        is lost. Avoids the overflow / overly-narrow-card issue on 768-1023px
        viewports that fixed 18rem side columns would cause.

        The center card uses `mx-auto` so it stays visually centered both in
        single-column mode AND in the lg 3-col mode. */}
        {/* `[align-items:safe_center]` keeps the card vertically centered when
          it fits, but flips to top-align when content overflows so users
          can scroll to the very top — without it `lg:items-center` clips
          the upper rows of long lists (e.g. the breed grid for letter B). */}
        <div
          className="flex-1 flex items-start lg:[align-items:safe_center] justify-center px-4 py-4 min-h-0 overflow-y-auto funnel-bubble-clearance"
          data-funnel-scroll
        >
          {WIDE_KEYS.has(maxWidth) ? (
            // Modo ancho: sin grid de 3 columnas + phantom. La card usa
            // directamente max-w-4xl/5xl. Para pantallas como P9 que ya no
            // tienen sidebar y necesitan espacio horizontal para layouts
            // simétricos en 2 cols.
            <div className={`bg-white rounded-[20px] w-full ${widthClass} p-5 sm:p-6 mx-auto`}>{children}</div>
          ) : (
            <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-[18rem_minmax(0,32rem)_18rem] gap-4 lg:gap-6 items-start mx-auto">
              <div className="hidden lg:block" aria-hidden="true" />
              <div className={`bg-white rounded-[20px] w-full ${widthClass} p-5 sm:p-6 mx-auto`}>{children}</div>
              <aside className="hidden lg:block lg:sticky lg:top-4">{sidebar}</aside>
            </div>
          )}
        </div>

        {/* Bottom nav */}
        <div className="bg-white px-4 py-2.5 flex-shrink-0">
          <div className={`${widthClass} mx-auto`}>
            <div className="flex items-center justify-between">
              {/* Tipografía unificada con FunnelStickyCTA (Phase 2): mismo
                text-base sm:text-lg + font-medium en ambos botones. */}
              {showBack && step > 1 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-3 rounded-xl font-fredoka text-base sm:text-lg font-medium text-[#008BBC] hover:bg-[#008BBC]/5 transition-all"
                >
                  &larr; {t('layout.back', 'Atrás')}
                </button>
              ) : (
                <div />
              )}
              {showContinue && (
                <div className="flex flex-col items-end gap-1">
                  <button
                    type="button"
                    // No usamos `disabled` HTML para permitir capturar clicks
                    // en estado deshabilitado: cuando el usuario insiste,
                    // disparamos onContinueDisabledAttempt para que la
                    // pantalla marque los campos pendientes con borde rojo.
                    onClick={() => {
                      if (continueDisabled) {
                        onContinueDisabledAttempt?.();
                      } else {
                        onContinue?.();
                      }
                    }}
                    aria-disabled={continueDisabled}
                    aria-describedby={
                      continueDisabled && continueDisabledReason ? 'continue-disabled-reason' : undefined
                    }
                    className={`px-10 py-3 rounded-xl font-fredoka text-base sm:text-lg font-medium transition-all ${
                      !continueDisabled
                        ? 'bg-[#008BBC] text-white hover:bg-[#007aa8] shadow-md'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {continueText || t('layout.continue', 'Continuar')} &rarr;
                  </button>
                  {continueDisabled && continueDisabledReason && (
                    <p
                      id="continue-disabled-reason"
                      className="max-w-[220px] text-right font-poppins text-[11px] leading-snug text-amber-700"
                    >
                      {continueDisabledReason}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <PetFabTab petCount={petsCompleted.length} currentStep={currentStep} onOpen={() => setIsDrawerOpen(true)} />
      <PetDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        petsCompleted={petsCompleted}
        formData={formData}
        onUpdateCurrentPet={updateFormData}
        onUpdateCompletedPet={updatePetSnapshotData}
        onEditCompletedField={handleEditCompletedPet}
        onRemoveCurrentPet={handleRemoveCurrentPet}
        onRemoveCompletedPet={removePetSnapshot}
        firstPetId={firstPetId}
        isCheckout={petsCompleted.length > 0}
      />
    </>
  );
};
